import React, { useEffect, useState } from "react";
import {
  Form,
  Input,
  Button,
  Table,
  Switch,
  Row,
  Col,
  Card,
  Image,
  Space,
  Spin,
  message,
  Popconfirm,
  Modal,
  InputNumber,
} from "antd";
import { DeleteFilled, EditFilled, SubnodeOutlined } from "@ant-design/icons";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  getDealerById,
  getDealerDetailSummary,
  updateDealer,
  extendSubscription,
  deleteDealer,
} from "../../services/dealerService";
import { getDeviceByUserId, deleteDevice } from "../../services/deviceService";
import { useNavigate, useParams } from "react-router-dom";

import dayjs from "dayjs";
import AdvertResponse from "../../payload/response/AdvertResponse";
import ProductImage from "../../components/ProtectedImage/ProtectedImage";
import ProtectedImage from "../../components/ProtectedImage/ProtectedImage";
import DealerResponse from "../../payload/response/DealerResponse";
import { deleteAdvert, updateAdvertStatus } from "../../services/advertService";
import WidgetCard from "../../components/WidgetCard/WidgetCard";
import adverts from "../../assets/image/adverts";
import products from "../../assets/image/products";
import { onError } from "../../helper/helper";

const DealerDetail = () => {
  const { id } = useParams();
  const [form] = Form.useForm();
  const navigation = useNavigate();
  const queryClient = useQueryClient();
  const [advertList, setAdverts] = useState<AdvertResponse[]>([]);
  const [deviceList, setDeviceList] = useState<any[]>([]);
  const [extendModalVisible, setExtendModalVisible] = useState(false);
  const [extendForm] = Form.useForm();
  const { data, isPending, isFetching, isError, isSuccess } = useQuery({
    queryFn: () => getDealerById(Number(id)),
    queryKey: ["dealer" + id],
    retry: 1,
  });
  const { data: deviceData } = useQuery({
    queryFn: () => getDeviceByUserId(Number(id)),
    queryKey: ["devices" + id],
    retry: 1,
  });
  const {
    data: dealerSummary,
    isLoading: dealerSummaryLoading,
    isFetching: summaryFetching,
    error: er,
    refetch,
  } = useQuery({
    queryFn: () => getDealerDetailSummary(Number(id)),
    queryKey: ["dealerSummary" + id],
    retry: 1,
  });

  useEffect(() => {
    if (data?.entity.dealer) {
      form.setFieldsValue(data?.entity.dealer);
    }
    if (data?.entity.adverts) {
      setAdverts(data?.entity.adverts);
    }
  }, [isSuccess]);

  useEffect(() => {
    if (deviceData?.list) {
      setDeviceList(deviceData.list);
    }
  }, [deviceData]);

  if (isError) {
    navigation("/404");
  }
  const updateStatus = useMutation({
    mutationFn: (advertId: number) => {
      return updateAdvertStatus(advertId);
    },
    onSuccess: (data, variables) => {
      if (data?.isSuccessful) {
        let newAdverts = advertList.map((item) => {
          if (item.id === variables) {
            return { ...item, isActive: !item.isActive };
          }
          return item;
        });
        setAdverts(newAdverts);
        queryClient.invalidateQueries({
          queryKey: ["dealerSummary" + id],
        });
        message.success("İlan başarıyla güncellendi.");
      } else {
        message.error("İlan güncellenirken bir hata oluştu.");
      }
    },
  });

  const updateDealerForAdmin = useMutation({
    mutationFn: (dealer: DealerResponse) => {
      return updateDealer({ ...dealer, id: Number(id) });
    },

    onSuccess: () => {
      if (data?.isSuccessful) {
        message.success("Bayi başarıyla güncellendi.");
        queryClient.invalidateQueries({
          queryKey: ["dealer" + id],
        });
        queryClient.invalidateQueries({
          queryKey: ["dealerSummary" + id],
        });
      } else {
        message.error("Bayi güncellenirken bir hata oluştu.");
      }
    },
    onError: (error: any) => {
      onError(error);
    },
  });
  const handleDeleteAdvert = useMutation({
    mutationFn: (advertId: number) => {
      return deleteAdvert(advertId);
    },
    onSuccess: (data, variables) => {
      if (data?.isSuccessful) {
        let newAdverts = advertList.filter((item) => item.id !== variables);
        setAdverts(newAdverts);
        queryClient.invalidateQueries({
          queryKey: ["dealerSummary" + id],
        });
        message.success("İlan başarıyla silindi.");
      } else {
        message.error("İlan silinirken bir hata oluştu.");
      }
    },
    onError: (error: any) => {
      message.error("İlan silinirken bir hata oluştu.");
    },
  });

  const handleExtendSubscription = useMutation({
    mutationFn: ({ days, reason }: { days: number; reason: string }) => {
      return extendSubscription(
        Number(data?.entity.dealer.dealerId),
        days,
        reason
      );
    },
    onSuccess: (data) => {
      if (data?.isSuccessful) {
        message.success(
          `${extendForm.getFieldValue("days")} günlük ücretsiz uzatma yapıldı`
        );
        setExtendModalVisible(false);
        extendForm.resetFields();
        queryClient.invalidateQueries({
          queryKey: ["dealerSummary" + id],
        });
      } else {
        message.error("Abonelik uzatılırken hata oluştu");
      }
    },
    onError: () => {
      message.error("Abonelik uzatılırken hata oluştu");
    },
  });

  const handleDeleteDealer = useMutation({
    mutationFn: () => {
      return deleteDealer(Number(data?.entity.dealer.dealerId));
    },
    onSuccess: (response) => {
      if (response?.isSuccessful) {
        message.success("Bayi başarıyla silindi");
        navigation("/admin/dealers");
      } else {
        message.error(
          String(response?.exceptionMessage) ||
            "Bayi silinirken bir hata oluştu."
        );
      }
    },
    onError: (error: any) => {
      onError(error);
    },
  });

  const handleDeleteDevice = useMutation({
    mutationFn: (deviceId: number) => {
      return deleteDevice(deviceId);
    },
    onSuccess: (data, variables) => {
      if (data?.isSuccessful) {
        let newDevices = deviceList.filter((item) => item.id !== variables);
        setDeviceList(newDevices);
        queryClient.invalidateQueries({
          queryKey: ["devices" + id],
        });
        message.success("Cihaz başarıyla silindi.");
      } else {
        message.error("Cihaz silinirken bir hata oluştu.");
      }
    },
    onError: (error: any) => {
      message.error("Cihaz silinirken bir hata oluştu.");
    },
  });

  const checkAndDeleteDealer = () => {
    if (dealerSummaryLoading || summaryFetching) {
      return;
    }

    const hasActiveSubscription =
      dealerSummary?.entity?.subscription?.daysRemaining &&
      dealerSummary?.entity?.subscription?.daysRemaining > 0;

    if (hasActiveSubscription) {
      Modal.warning({
        title: "Uyarı",
        content:
          "Bu bayinin aktif aboneliği bulunmaktadır. Bayi silinemez. İsterseniz bu bayiyi pasif hale getirebilirsiniz.",
        okText: "Tamam",
      });
      return;
    }

    handleDeleteDealer.mutate();
  };
  const deviceColumns = [
    {
      title: "Cihaz ID",
      dataIndex: "id",
      key: "id",
    },
    {
      title: "Unique ID",
      dataIndex: "uniqueId",
      key: "uniqueId",
    },
    {
      title: "Cihaz Markası",
      dataIndex: "deviceBrand",
      key: "deviceBrand",
    },
    {
      title: "Cihaz ID",
      dataIndex: "deviceId",
      key: "deviceId",
    },
    {
      title: "İşletim Sistemi",
      dataIndex: "deviceOs",
      key: "deviceOs",
    },
    {
      title: "İS Versiyonu",
      dataIndex: "deviceOsVersion",
      key: "deviceOsVersion",
    },
    {
      title: "Platform",
      dataIndex: "devicePlatform",
      key: "devicePlatform",
    },
    {
      title: "İşlem",
      key: "action",
      render: (text: any, record: any) => {
        return (
          <Space>
            <Popconfirm
              placement="bottomLeft"
              title={"Silmek istediğinize emin misiniz?"}
              description="Bu işlem geri alınamaz ve cihaz silinecektir."
              onConfirm={() => {
                handleDeleteDevice.mutate(record.id);
              }}
              okText="Evet"
              cancelText="Hayır"
            >
              <Button type="primary" danger>
                <DeleteFilled />
              </Button>
            </Popconfirm>
          </Space>
        );
      },
    },
  ];
  const columns = [
    {
      title: "Resim",
      dataIndex: "productImage",
      key: "productImage",
      render: (text: any, record: AdvertResponse) => {
        return (
          <ProductImage
            imageUrl={record?.product.images?.[0]?.imageUrl || "error"}
            name={record.product.name}
          />
        );
      },
    },
    {
      title: "İlan Numarası",
      dataIndex: "adNumber",
      key: "adNumber",
      render: (text: any, record: AdvertResponse) => {
        return <span>{record.id}</span>;
      },
    },
    {
      title: "Ürün Adı",
      dataIndex: "productName",
      key: "productName",
      render: (text: any, record: AdvertResponse) => {
        return <span>{record.product.name}</span>;
      },
    },
    {
      title: "Kategori Adı",
      dataIndex: "categoryName",
      key: "categoryName",
      render: (text: any, record: AdvertResponse) => {
        return <span>{record.product.categoryName}</span>;
      },
    },

    {
      title: "Üretim Tarihi",
      dataIndex: "startDate",
      key: "startDate",
      render: (text: any, record: any) => {
        return (
          <Space>
            {record.startDate === null ? (
              <span>-</span>
            ) : (
              <span>{dayjs(record.startDate).format("DD.MM.YYYY")}</span>
            )}
          </Space>
        );
      },
    },
    {
      title: "Son Kullanım Tarihi",
      dataIndex: "expiryDate",
      key: "expiryDate",
      render: (text: any, record: any) => {
        return (
          <Space>
            <span>{dayjs(record.expiryDate).format("DD.MM.YYYY")}</span>
          </Space>
        );
      },
    },
    {
      title: "Yayınlanma Tarihi",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (text: any, record: any) => {
        return (
          <Space>
            <span>{dayjs(record.createdAt).format("DD.MM.YYYY")}</span>
          </Space>
        );
      },
    },
    {
      title: "Durum",
      dataIndex: "isActive",
      key: "isActive",
      render: (text: any, record: any) => {
        return (
          <Space>
            <Popconfirm
              placement="bottomLeft"
              title={`İlanı ${
                record.isActive ? "pasif" : "aktif"
              } hale getirmek istediğinize emin misiniz?`}
              description="İlanı aktif hale getirmek istediğinize emin misiniz?"
              onConfirm={() => {
                updateStatus.mutate(record.id);
              }}
              okText="Evet"
              cancelText="Hayır"
            >
              <Switch
                checkedChildren="Aktif"
                unCheckedChildren="Pasif"
                onChange={(value) => {}}
                checked={record.isActive}
              />
            </Popconfirm>
          </Space>
        );
      },
    },
    {
      title: "İşlem",
      key: "action",
      render: (text: any, record: AdvertResponse) => {
        return (
          <Space>
            <Popconfirm
              placement="bottomLeft"
              title={"Silmek istediğinize emin misiniz?"}
              description="Bu işlem geri alınamaz ve tüm veriler silinecektir."
              onConfirm={() => {
                handleDeleteAdvert.mutate(record.id);
              }}
              okText="Evet"
              cancelText="Hayır"
            >
              <Button type="primary" danger>
                <DeleteFilled />
              </Button>
            </Popconfirm>
          </Space>
        );
      },
    },
  ];
  const handleFormSubmit = (values: any) => {
    updateDealerForAdmin.mutate(values);
  };
  return (
    <div style={{ padding: "20px", backgroundColor: "#f0f2f5" }}>
      <Spin spinning={isPending || isFetching}>
        <Row
          wrap
          gutter={[16, 16]}
          style={{ marginBottom: "20px" }}
          justify="end"
          align="top"
        >
          <Col xs={12} sm={8} md={6} lg={4} xl={3}>
            <Popconfirm
              title="Bayiyi silmek istediğinize emin misiniz?"
              description="Bu işlem geri alınamaz ve tüm veriler silinecektir."
              onConfirm={() => checkAndDeleteDealer()}
              okText="Evet"
              cancelText="Hayır"
            >
              <Button type="primary" danger style={{ width: "100%" }}>
                Bayiyi Sil
              </Button>
            </Popconfirm>
          </Col>
        </Row>
        <Row
          wrap
          gutter={[16, 16]}
          style={{ marginBottom: "20px" }}
          justify="space-between"
          align="top"
        >
          <Col xs={24} sm={24} md={24}>
            <Card
              style={{
                backgroundColor: "white",
                borderRadius: 8,
                boxShadow: "5px 8px 24px 5px rgba(0,0,0,0.1)",
                border: "1px solid #f0f0f0",
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 20 }}>
                  <div
                    style={{
                      height: 48,
                      width: 48,
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      borderRadius: 8,
                      backgroundColor: "#1890ff",
                    }}
                  >
                    <SubnodeOutlined
                      style={{
                        color: "#fff",
                        fontSize: "24px",
                      }}
                    />
                  </div>
                  <div
                    style={{ gap: 5, display: "flex", flexDirection: "column" }}
                  >
                    <span
                      style={{
                        color: "#8c8c8c",
                        fontSize: 14,
                        fontWeight: 900,
                      }}
                    >
                      Abonelik Bilgileri
                    </span>
                    <span
                      style={{
                        margin: 0,
                        fontWeight: "700",
                        fontSize: 30,
                        color: dealerSummary?.entity?.subscription?.color,
                      }}
                    >
                      {(dealerSummary?.entity?.subscription?.daysRemaining?.toString() ||
                        0) + " Gün"}
                    </span>
                  </div>
                </div>
                <Button
                  type="primary"
                  onClick={() => setExtendModalVisible(true)}
                  style={{ backgroundColor: "#52c41a", borderColor: "#52c41a" }}
                >
                  Abonelik Uzat
                </Button>
              </div>
            </Card>
          </Col>
          <Col xs={24} sm={12} md={12}>
            <WidgetCard
              loading={dealerSummaryLoading || summaryFetching}
              title="Toplam yayındaki ilan sayısı"
              value={dealerSummary?.entity.totalActiveAdvert.toString()}
              icon={adverts}
            />
          </Col>
          <Col xs={24} sm={12} md={12}>
            <WidgetCard
              loading={dealerSummaryLoading || summaryFetching}
              title="Toplam yayında olmayan ilan sayısı"
              value={dealerSummary?.entity.totalInactiveAdvert.toString()}
              icon={adverts}
            />
          </Col>
        </Row>
        <Card
          title="Bayi Bilgileri"
          bordered={true}
          style={{ marginBottom: "20px", background: "#fff" }}
        >
          <div style={{ marginBottom: "20px" }}>
            <ProtectedImage
              imageUrl={data?.entity.dealer?.companyImage || "error"}
              name={
                data?.entity.dealer.firstName +
                " " +
                data?.entity.dealer.lastName
              }
            />
          </div>
          <Form form={form} layout="vertical" onFinish={handleFormSubmit}>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="firstName" label="Ad">
                  <Input />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="lastName" label="Soyad">
                  <Input />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="email" label="E-posta">
                  <Input />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="phone" label="Telefon">
                  <Input />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="taxNumber" label="Vergi Numarası">
                  <Input />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="taxOffice" label="Vergi Dairesi">
                  <Input />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="glnNumber" label="GLN Numarası">
                  <Input />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="companyName" label="Firma Adı">
                  <Input />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="isActive"
                  label="Aktif Mi?"
                  valuePropName="checked"
                >
                  <Switch />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="isApproved"
                  label="Onaylı Mı?"
                  valuePropName="checked"
                >
                  <Switch disabled />
                </Form.Item>
              </Col>
            </Row>

            <Row>
              <Col span={24}>
                <Form.Item name="address" label="Adres">
                  <Input.TextArea rows={3} style={{ width: "100%" }} />
                </Form.Item>
              </Col>
            </Row>

            <Button type="primary" htmlType="submit">
              Güncelle
            </Button>
          </Form>
        </Card>

        <Card
          title="Cihazlar"
          bordered={true}
          style={{ background: "#fff", marginBottom: "20px" }}
        >
          <Table
            loading={isPending || isFetching}
            scroll={{ x: 1300 }}
            columns={deviceColumns}
            dataSource={deviceList.map((item: any) => ({
              ...item,
              key: item.id,
            }))}
          />
        </Card>

        <Card title="İlanlar" bordered={true} style={{ background: "#fff" }}>
          <Table
            loading={isPending || isFetching}
            scroll={{ x: 1300 }}
            columns={columns}
            dataSource={advertList.map((item: any) => ({
              ...item,
              key: item.id,
            }))}
          />
        </Card>
      </Spin>

      <Modal
        title="Abonelik Uzatma"
        open={extendModalVisible}
        onCancel={() => {
          setExtendModalVisible(false);
          extendForm.resetFields();
        }}
        footer={null}
      >
        <Form
          form={extendForm}
          layout="vertical"
          onFinish={(values) => {
            handleExtendSubscription.mutate({
              days: values.days,
              reason: values.reason,
            });
          }}
        >
          <Form.Item
            name="days"
            label="Uzatma Günü"
            rules={[
              { required: true, message: "Lütfen uzatma günü giriniz" },
              { type: "number", min: 1, message: "En az 1 gün olmalıdır" },
            ]}
          >
            <InputNumber
              style={{ width: "100%" }}
              placeholder="Uzatma günü"
              min={1}
            />
          </Form.Item>
          <Form.Item
            name="reason"
            label="Uzatma Sebebi"
            rules={[
              { required: true, message: "Lütfen uzatma sebebi giriniz" },
            ]}
          >
            <Input.TextArea
              rows={3}
              placeholder="Uzatma sebebini açıklayınız"
            />
          </Form.Item>
          <Form.Item>
            <Space>
              <Button
                type="primary"
                htmlType="submit"
                loading={handleExtendSubscription.isPending}
              >
                Uzat
              </Button>
              <Button
                onClick={() => {
                  setExtendModalVisible(false);
                  extendForm.resetFields();
                }}
              >
                İptal
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default DealerDetail;
